import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timelineService } from '../supabase/timeline';
import type { PlotLine, PlotCard, CardType } from '@/pages/timeline/types';
import { chapterKeys } from '@/hooks/useChapters';

// Define cache times - balanced caching to allow updates while preventing excessive refetches
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
// Reasonable stale times that allow for updates when navigating between pages
const STALE_TIME = HOUR_IN_MS; // 1 hour stale time - allows refetch when data might be outdated
const GC_TIME = DAY_IN_MS * 7; // 7 days garbage collection

// Define query keys for better cache management
export const timelineKeys = {
  all: ['timeline'] as const,
  timeline: (projectId: string, storyId?: string) =>
    [...timelineKeys.all, projectId, storyId] as const,
  plotCards: (timelineId: string) =>
    [...timelineKeys.all, 'plotCards', timelineId] as const,
  plotLines: (timelineId: string) =>
    [...timelineKeys.all, 'plotLines', timelineId] as const,
  chapters: (storyId: string) =>
    [...timelineKeys.all, 'chapters', storyId] as const,
  settings: (timelineId: string) =>
    [...timelineKeys.all, 'settings', timelineId] as const,
  chapterCards: (chapterId: number, timelineId?: string) =>
    [...timelineKeys.all, 'chapterCards', chapterId, timelineId] as const,
  secureTimeline: (projectId: string, storyId?: string) =>
    [...timelineKeys.all, 'secure', projectId, storyId] as const,
};

// Query hooks with improved caching
export function useTimeline(projectId: string, storyId?: string) {
  return useQuery({
    queryKey: timelineKeys.timeline(projectId, storyId),
    queryFn: () => timelineService.getTimeline(projectId, storyId),
    staleTime: STALE_TIME, // Extremely long stale time to prevent refetches
    gcTime: GC_TIME, // Much longer cache time
    refetchOnMount: false, // Never refetch on mount
    refetchOnWindowFocus: false, // Never refetch on window focus
    refetchOnReconnect: false, // Never refetch on reconnect
  });
}

export function usePlotCards(timelineId: string) {
  return useQuery({
    queryKey: timelineKeys.plotCards(timelineId),
    queryFn: () => timelineService.getPlotCardsNoCache(timelineId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function usePlotLines(timelineId: string) {
  return useQuery({
    queryKey: timelineKeys.plotLines(timelineId),
    queryFn: () => timelineService.getPlotLines(timelineId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useChapters(storyId: string) {
  return useQuery({
    queryKey: timelineKeys.chapters(storyId),
    queryFn: () => timelineService.getChaptersNoCache(storyId),
    staleTime: 0, // Always consider data stale to ensure fresh data when navigating
    gcTime: GC_TIME,
    refetchOnMount: true, // Refetch on mount to get latest data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useTimelineSettings(timelineId: string) {
  return useQuery({
    queryKey: timelineKeys.settings(timelineId),
    queryFn: () => timelineService.getTimelineSettingsNoCache(timelineId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useChapterPlotCards(chapterId: number, timelineId?: string) {
  return useQuery({
    queryKey: timelineKeys.chapterCards(chapterId, timelineId),
    queryFn: () => timelineService.getPlotCardsByChapter(chapterId, timelineId),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!chapterId, // Only run if chapterId is provided
  });
}

// Mutation hooks
export function useCreatePlotCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      timelineId,
      plotLineId,
      title,
      content,
      cardType,
      chapterId,
      position,
    }: {
      timelineId: string;
      plotLineId: string;
      title: string;
      content: string;
      cardType: CardType;
      chapterId?: number;
      position?: number;
    }) =>
      timelineService.createPlotCard(
        timelineId,
        plotLineId,
        title,
        content,
        cardType,
        chapterId,
        position
      ),
    onSuccess: (newCard, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(variables.timelineId) });
      queryClient.invalidateQueries({ queryKey: timelineKeys.timeline(variables.timelineId) });

      // If chapterId is provided, invalidate ALL queries for this chapter (regardless of timelineId)
      if (variables.chapterId) {
        queryClient.invalidateQueries({
          queryKey: [...timelineKeys.all, 'chapterCards', variables.chapterId]
        });
      }
    },
  });
}

export function useUpdatePlotCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      updates,
      timelineId
    }: {
      cardId: string;
      updates: Partial<PlotCard>;
      timelineId?: string;
    }) =>
      timelineService.updatePlotCard(cardId, updates, timelineId),
    onSuccess: (updatedCard, variables) => {
      // Determine the timelineId, either from the variables or from the card's x property
      const timelineId = variables.timelineId || updatedCard.x.toString();

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(timelineId) });

      // If chapter_id changed or if we just want to be safe, invalidate generic chapter queries
      // We invalidate broad chapter keys to ensure sidebar (which has no timeline filter) updates
      queryClient.invalidateQueries({
        queryKey: [...timelineKeys.all, 'chapterCards']
      });
    },
  });
}

export function useDeletePlotCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, timelineId }: { cardId: string; timelineId: string }) =>
      timelineService.deletePlotCard(cardId, timelineId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(variables.timelineId) });
      queryClient.invalidateQueries({ queryKey: timelineKeys.timeline(variables.timelineId) });

      // Invalidate all chapter cards queries to be safe
      queryClient.invalidateQueries({
        queryKey: [...timelineKeys.all, 'chapterCards']
      });
    },
  });
}

export function useCreatePlotLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ timelineId, title, color }: { timelineId: string; title: string; color: string }) =>
      timelineService.createPlotLine(timelineId, title, color),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotLines(variables.timelineId) });
      queryClient.invalidateQueries({ queryKey: timelineKeys.timeline(variables.timelineId) });
    },
  });
}

export function useUpdatePlotLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineId, updates, timelineId }: {
      lineId: string;
      updates: Partial<PlotLine>;
      timelineId: string;
    }) =>
      timelineService.updatePlotLine(lineId, updates),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotLines(variables.timelineId) });
      queryClient.invalidateQueries({ queryKey: timelineKeys.timeline(variables.timelineId) });
    },
  });
}

export function useDeletePlotLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineId, timelineId }: { lineId: string; timelineId: string }) =>
      timelineService.deletePlotLine(lineId, timelineId),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotLines(variables.timelineId) });
      queryClient.invalidateQueries({ queryKey: timelineKeys.timeline(variables.timelineId) });

      // Also invalidate plot cards since they might reference this plot line
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(variables.timelineId) });
    },
  });
}

export function useUpdateTimelineSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ timelineId, updates }: {
      timelineId: string;
      updates: any;
    }) =>
      timelineService.updateTimelineSettings(timelineId, updates),
    onSuccess: (_, variables) => {
      // Invalidate settings query
      queryClient.invalidateQueries({ queryKey: timelineKeys.settings(variables.timelineId) });

      // Also invalidate the timeline query since it includes settings
      queryClient.invalidateQueries({
        queryKey: timelineKeys.all.concat('timeline'),
        predicate: (query) => {
          // Only invalidate timeline queries that include this timelineId
          const queryKey = query.queryKey as Array<string>;
          return queryKey.includes(variables.timelineId);
        },
      });
    },
  });
}

export function useMoveCardsToChapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardIds,
      chapterId,
      timelineId
    }: {
      cardIds: number[];
      chapterId: number | null;
      timelineId: string;
    }) =>
      timelineService.moveCardsToChapter(cardIds, chapterId, timelineId),
    onSuccess: (_, variables) => {
      // Invalidate plot cards
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(variables.timelineId) });

      // Invalidate all chapter cards queries
      queryClient.invalidateQueries({
        queryKey: [...timelineKeys.all, 'chapterCards']
      });

      // Also invalidate the timeline
      queryClient.invalidateQueries({
        queryKey: timelineKeys.all.concat('timeline'),
        predicate: (query) => {
          // Only invalidate timeline queries that include this timelineId
          const queryKey = query.queryKey as Array<string>;
          return queryKey.includes(variables.timelineId);
        },
      });
    },
  });
}

export function useUpdatePlotCardPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      newPosition,
      timelineId,
      chapterId
    }: {
      cardId: string;
      newPosition: number;
      timelineId: string;
      chapterId: number | null;
    }) =>
      timelineService.updatePlotCardPosition(cardId, newPosition, timelineId, chapterId),
    onSuccess: (_, variables) => {
      // Invalidate plot cards
      queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(variables.timelineId) });

      // If chapterId is provided, invalidate ALL queries for this chapter
      if (variables.chapterId) {
        queryClient.invalidateQueries({
          queryKey: [...timelineKeys.all, 'chapterCards', variables.chapterId]
        });
      }
    },
  });
}

export function useReorderChapters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storyId, chapterIds }: { storyId: string; chapterIds: number[] }) =>
      timelineService.reorderChapters(storyId, chapterIds),
    onSuccess: (_, variables) => {
      // Invalidate chapters
      queryClient.invalidateQueries({ queryKey: timelineKeys.chapters(variables.storyId) });

      // Invalidate the content chapters query used by sidebar and chapter list
      queryClient.invalidateQueries({
        queryKey: chapterKeys.list(variables.storyId)
      });

      // Also invalidate all timeline queries for this story
      queryClient.invalidateQueries({
        queryKey: timelineKeys.all.concat('timeline'),
        predicate: (query) => {
          // Only invalidate timeline queries that include this storyId
          const queryKey = query.queryKey as Array<string>;
          return queryKey.includes(variables.storyId);
        },
      });
    },
  });
}

export function useReorderTimelines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storyId, timelineIds }: { storyId: string; timelineIds: number[] }) =>
      timelineService.reorderTimelines(storyId, timelineIds),
    onSuccess: (_, variables) => {
      // Invalidate all timeline and plot line queries for this story
      queryClient.invalidateQueries({
        queryKey: timelineKeys.all.concat('timeline'),
        predicate: (query) => {
          // Only invalidate timeline queries that include this storyId
          const queryKey = query.queryKey as Array<string>;
          return queryKey.includes(variables.storyId);
        },
      });

      // Invalidate plot lines for all timelines in the list
      variables.timelineIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: timelineKeys.plotLines(id.toString()) });
      });
    },
  });
}

// Utility function to refresh all data
export function useRefreshTimeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, storyId }: { projectId: string; storyId?: string }) =>
      timelineService.refreshAllData(projectId, storyId),
    onSuccess: (newData, variables) => {
      // Update the timeline query data
      queryClient.setQueryData(
        timelineKeys.timeline(variables.projectId, variables.storyId),
        newData
      );

      // Also invalidate all related queries
      if (newData.storyId) {
        queryClient.invalidateQueries({ queryKey: timelineKeys.chapters(newData.storyId) });
      }

      if (newData.timeline?.id) {
        queryClient.invalidateQueries({ queryKey: timelineKeys.settings(newData.timeline.id) });
        queryClient.invalidateQueries({ queryKey: timelineKeys.plotLines(newData.timeline.id) });
        queryClient.invalidateQueries({ queryKey: timelineKeys.plotCards(newData.timeline.id) });
      }
    },
  });
}

/**
 * Hook to get the entire timeline data in a single call
 * This uses the get_secure_timeline_data RPC function
 */
export function useSecureTimeline(projectId: string, storyId?: string) {
  return useQuery({
    queryKey: timelineKeys.secureTimeline(projectId, storyId),
    queryFn: () => timelineService.getSecureTimelineData(projectId),
    staleTime: 0, // Always consider data stale to ensure fresh data when navigating
    gcTime: GC_TIME,
    refetchOnMount: true, // Refetch on mount to get latest data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Enhanced hook to get and transform the unified timeline data in a single call
 * This uses the get_secure_timeline_data RPC function and transforms the result
 * to match the format expected by the Timeline component
 */
export function useUnifiedTimeline(projectId: string, storyId?: string) {
  const { data, isLoading, error, ...rest } = useQuery({
    queryKey: [...timelineKeys.secureTimeline(projectId, storyId), 'transformed'],
    queryFn: async () => {
      // Get the raw secure timeline data
      const rawData = await timelineService.getSecureTimelineData(projectId);

      // Transform it to the expected format
      return timelineService.transformSecureTimelineData(rawData, projectId);
    },
    staleTime: 0, // Always consider data stale to ensure fresh data when navigating
    gcTime: GC_TIME,
    refetchOnMount: true, // Refetch on mount to get latest data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { data, isLoading, error, ...rest };
}